import React, { useState } from 'react';
import { createAlertRule, updateAlertRule } from '../../../services/api';
import { fetchRuleImage } from '../../../services/alertsApi';
import { AlertRule, AlertCondition } from '../../../types';
import { blankCondition, blankRule } from '../surveillanceShared';

interface Options {
    /** Körs efter att en regel sparats (t.ex. ladda om listan). */
    onSaved: () => Promise<void> | void;
    onError: (message: string) => void;
}

/** State och hanterare för larmregel-formuläret: ny, ändra, spara, avbryt. */
export function useRuleForm({ onSaved, onError }: Options) {
    const [showForm, setShowForm] = useState(false);
    const [editRuleId, setEditRuleId] = useState<number | null>(null);
    const [form, setForm] = useState(blankRule());

    const openCreate = (zoneId: number | null = null) => {
        setEditRuleId(null);
        setForm({ ...blankRule(), zone_id: zoneId });
        setShowForm(true);
    };

    const openEdit = (rule: AlertRule) => {
        setEditRuleId(rule.id);
        let condition_groups: { logic: 'AND' | 'OR'; conditions: AlertCondition[] }[];

        if (rule.condition_groups && rule.condition_groups.length > 0) {
            condition_groups = rule.condition_groups;
        } else {
            // Gamla platta villkor blir en enda grupp
            let conditions: AlertCondition[] = rule.conditions && rule.conditions.length > 0
                ? rule.conditions
                : (rule.devices ?? []).flatMap(d =>
                    (rule.properties ?? (rule.property ? [rule.property] : [])).map(p => ({
                        device_ieee: d.ieee_address,
                        property: p,
                        operator: rule.operator,
                        threshold: rule.threshold,
                    } as AlertCondition))
                  );
            if (conditions.length === 0) conditions = [blankCondition()];
            condition_groups = [{ logic: rule.condition_logic ?? 'AND', conditions }];
        }
        setForm({
            name: rule.name,
            zone_id: rule.zone_id ?? null,
            group_name: rule.group_name ?? '',
            condition_groups,
            group_logic: rule.group_logic ?? 'OR',
            cooldown_minutes: rule.cooldown_minutes,
            trigger_threshold: rule.trigger_threshold ?? 1,
            trigger_mode: rule.trigger_mode ?? 'count',
            contact_ids: rule.contacts.map(c => c.id),
            instruction_text: rule.instruction_text ?? '',
            instruction_image: '',  // hämtas nedan; listan skickar inte med stora bilder
            imageModified: false,
            requires_deviation_report: rule.requires_deviation_report ?? false,
            applies_to_all_devices: rule.applies_to_all_devices ?? false,
        });
        setShowForm(true);
        // Hämta bilden bara om regeln har en
        if (rule.instruction_has_image) {
            fetchRuleImage(rule.id).then(img => {
                setForm(prev => ({ ...prev, instruction_image: img ?? '' }));
            });
        }
    };

    const cancel = () => setShowForm(false);

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        const validGroups = form.condition_groups
            .map(g => ({ ...g, conditions: g.conditions.filter(c => (c.device_ieee || form.applies_to_all_devices) && c.property) }))
            .filter(g => g.conditions.length > 0);
        if (validGroups.length === 0) { onError('Lägg till minst ett komplett villkor.'); return; }
        // Platt lista för de gamla kolumnerna (bakåtkompatibelt)
        const flatConditions = validGroups.flatMap(g => g.conditions);
        // instruction_image skickas bara om användaren ändrat bilden i den här sessionen;
        // annars lämnas värdet i databasen orört.
        // contact_ids utelämnas med flit: mottagare redigeras från Kontakter, och
        // updateRule rör bara kopplingarna när fältet faktiskt skickas. Skickade vi det
        // härifrån skulle ett formulär som stått öppet ett tag skriva över en tilldelning
        // som gjorts under tiden.
        const { imageModified, instruction_image, contact_ids, ...formBase } = form;
        const payload = {
            ...formBase,
            ...(imageModified ? { instruction_image } : {}),
            zone_id: form.zone_id,
            condition_groups: validGroups,
            group_logic: form.group_logic,
            conditions: flatConditions,
            condition_logic: validGroups.length === 1 ? validGroups[0].logic : form.group_logic,
        };
        try {
            if (editRuleId !== null) { await updateAlertRule(editRuleId, payload); }
            else { await createAlertRule(payload); }
            setShowForm(false);
            await onSaved();
        } catch (err: any) { onError(err.message); }
    };

    return { showForm, editRuleId, form, setForm, openCreate, openEdit, cancel, submit };
}
